import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../../../../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { PARENT_TO_CHILD_IFRAME_EVENTS, PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS, PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME, WIDGET_CONSTANTS } from '../../../../constants/widget-constants';
import { StoresDao } from '../../../../db-models/stores-dao';
import { UserDao } from '../../../../db-models/user-data-dao';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { Partner } from '../../../../models/global';
import { AppointmentState } from '../../../../models/state.model';
import { WidgetColorConf } from '../../../../models/widget-color.model';
import { CustomEventService } from '../../../../services/custom-event.service';
import { HelperService } from '../../../../services/helper.service';
import { LoggerService } from '../../../../services/logger.service';
import { PartnerService } from '../../../../services/partner.service';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { NewAlertComponent } from '../../../../shared/components/new-alert/new-alert.component';
import { AddressFormatPipe } from '../../../../shared/pipes/address-format.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';
import { CwCheckboxComponent } from '../../../common/theme/cw-checkbox/cw-checkbox.component';
import { CwRadioTickButtonComponent } from '../../../common/theme/cw-radio-tick-button/cw-radio-tick-button.component';

const pipes = [AddressFormatPipe, TranslationPipe, TrustHtmlPipe];
const components = [CwRadioTickButtonComponent, ButtonComponent, CwCheckboxComponent, NewAlertComponent];
const modules = [FormsModule, NgClass, TranslateModule];

@Component({
  selector: 'app-appointment-worker-card',
  templateUrl: './appointment-worker-card.component.html',
  styleUrls: ['./appointment-worker-card.component.scss'],
  imports: [...modules, ...components, ...pipes]
})
export class AppointmentWorkerCardComponent implements OnInit, OnChanges {

  @Input() lang: string;
  @Input() workersAvailable: UserDao[] = [];
  @Input() appointmentState: AppointmentState;
  @Input() widgetColorConf: WidgetColorConf;
  @Input() showEgalWer: boolean;
  @Input() partner: Partner;
  @Input() nextButtonTemplate: CustomBookingMessageTemplate;
  @Input() previousButtonTemplate: CustomBookingMessageTemplate;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() subscriptionTypeId: number;
  @Input() viewMode: string;
  @Input() workerException: string[] = [];

  @Output() navigateToEvent = new EventEmitter<any>();
  @Output() selectWorkerEvent = new EventEmitter<any>();
  @Output() workerSelectedEvent = new EventEmitter<any>();
  @Output() resetFormEvent = new EventEmitter<any>();
  @Output() workerExceptionEvent = new EventEmitter<any>();

  readonly environment = environment;
  readonly appointmentConstant = APPOINTMENT_CONSTANT;
  readonly widgetConstant = WIDGET_CONSTANTS;
  readonly workerAvatarUrl = environment.workerAvatarUrl;
  readonly baseUrl = environment.baseUrl;
  readonly parentToChildIframeEvents = PARENT_TO_CHILD_IFRAME_EVENTS;
  readonly parentToChildIframeEventActions = PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS;
  readonly parentToChildIframeValidationControlName = PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME;
  readonly templateContent = TRANSLATION_TEMPLATES;

  isMobile: boolean;
  protected exceptionWorkerList: string[] = [];
  protected isExceptionEnabled = false;
  widgetAnyoneLabelTemplate: CustomBookingMessageTemplate;
  widgetAnyoneDescTemplate: CustomBookingMessageTemplate;
  widgetResourceHeadingTemplate: CustomBookingMessageTemplate;
  protected stepErrorTemplate: CustomBookingMessageTemplate;
  protected readonly showStepValidationError = signal(false);

  constructor(
    private deviceDetectorService: DeviceDetectorService,
    private translate: TranslateService,
    private helperService: HelperService,
    private customEventService: CustomEventService,
    private widgetUtilService: WidgetUtilService,
    private partnerService: PartnerService,
  ) {
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);
  }

  protected isWhitelabel(): boolean {
    return this.partnerService.hasFeature('is_whitelabel_enabled');
  }

  ngOnInit() {
    this.isMobile = this.deviceDetectorService.isMobile();
    if (this.workerException.length) {
      this.isExceptionEnabled = true;
      this.exceptionWorkerList = this.workerException;
      this.workersAvailable.forEach(worker => worker.checked = this.exceptionWorkerList.includes(worker.uuid));
    }
    this.workersAvailable.sort(this.helperService.sortArrayObjectsByField('resource_name'));
    this.appointmentState?.default_worker_id && this.selectWorker(this.appointmentState.default_worker_id);

    this.customEventService.iFrameNavigationFromParent.subscribe(action => {
      switch (action) {
        case this.parentToChildIframeEventActions.PREVIOUS:
          this.navigateTo(this.appointmentConstant.APPOINTMENT);
          break;

        case this.parentToChildIframeEventActions.NEXT:
          if (this.appointmentState.worker !== null) {
            this.navigateTo(this.appointmentConstant.DATE);
          } else if (this.appointmentState.worker === 0 || this.appointmentState.worker === null) {
            this.widgetUtilService.sendMessageToParent({
              eventName: this.parentToChildIframeEvents.VALIDATION,
              controls: [
                this.parentToChildIframeValidationControlName.WORKER
              ]
            });
          }
          break;
      }
    });

    // When length of storeDefaultWorkerIds is > 0 means, store has multiple mapped workers which needs to be filtered out first
    // before moving further
    if (this.appointmentState.storeDefaultWorkerIds?.length) {
      const filteredWorker = this.workersAvailable.filter(worker => this.appointmentState.storeDefaultWorkerIds.includes(worker.id));
      if (filteredWorker?.length) {
        LoggerService.warn('[Default store worker]: Default store workers are present so overwriting all the supported workers by default worker');
        this.workersAvailable = filteredWorker;
      }
    }

    if (this.customEventService.previousPage !== APPOINTMENT_CONSTANT.DATE) {
      // When is_widget_postcode_worker_random_selection_enabled setting is enable and default worker from postcode
      // is null then worker selection will be random and selection will be skipped and if default worker is not null
      // then default will be selected selection will be skipped.
      if (Number(this.widgetUtilService.widgetConf.is_widget_postcode_worker_random_selection_enabled) === 1) {
        if (this.appointmentState.default_worker_id === null && this.workersAvailable?.length) {
          const randomIndex = Math.floor(Math.random() * this.workersAvailable.length);
          const workerId = this.workersAvailable[randomIndex].id;
          this.selectWorkerEvent.emit(workerId);
          this.navigateTo(this.appointmentConstant.DATE);
        } else if (this.appointmentState.default_worker_id) {
          this.selectWorkerEvent.emit(Number(this.appointmentState.default_worker_id));
          this.navigateTo(this.appointmentConstant.DATE);
        }
      } else {
        if (this.appointmentState.default_worker_id) {
          this.selectWorkerEvent.emit(Number(this.appointmentState.default_worker_id));
          this.navigateTo(this.appointmentConstant.DATE);
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupTemplates();
    }
  }

  selectWorker(workerId: number): void {
    this.selectWorkerEvent.emit(workerId);
  }

  // Store shown on the worker-selection card: the customer's selected store
  // (appointmentState.store, resolved from the stores this worker serves) with
  // the worker's primary store as fallback — single-store partners and any
  // no-selection path render exactly as before.
  getSelectedStore(worker: UserDao): StoresDao | undefined {
    const selectedStoreId = this.appointmentState?.store;
    if (selectedStoreId != null && worker?.stores?.length) {
      const selected = worker.stores.find(store => Number(store.id) === Number(selectedStoreId));
      if (selected) {
        return selected;
      }
    }
    return worker?.store;
  }

  onWorkerSelected(): void {
    this.workerSelectedEvent.emit();
  }

  navigateTo(page: string, isHavingMeetingProviders = false): void {
    const workerExcpetionDetails = {
      isExceptionEnabled: this.isExceptionEnabled,
      exceptionalWorkers: this.exceptionWorkerList
    }
    this.workerExceptionEvent.emit(workerExcpetionDetails);
    if (isHavingMeetingProviders) {
      this.navigateToEvent.next(this.appointmentConstant.MEETING_PROVIDERS);
    } else {
      this.navigateToEvent.next(page);
    }
  }

  previousNavigation(page: string): void {
    this.navigateToEvent.next(page);
  }

  protected onNextButtonClick(): void {
    if (Number(this.partner?.widget_buttons_always_enabled) === 1 && this.appointmentState.worker === null) {
      this.showStepValidationError.set(true);
      return;
    }
    this.showStepValidationError.set(false);
    this.navigateTo(this.appointmentConstant.DATE, this.appointmentState?.has_meeting_types_restrictions);
  }

  resetForm(): void {
    this.resetFormEvent.emit();
  }

  toggleException(): void {
    this.isExceptionEnabled = !this.isExceptionEnabled;
  }

  onSelectCheckbox(event: Event, workerId: string): void {
    const target = event.target as HTMLInputElement;
    if (target?.checked) {
      this.exceptionWorkerList.push(workerId);
    } else {
      this.exceptionWorkerList = this.exceptionWorkerList.filter(id => id !== workerId);
    }
  }

  setupTemplates(): void {
    this.widgetAnyoneLabelTemplate = this.widgetTemplates.find(template =>
      template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_ANYONE_LABEL
    );
    this.widgetAnyoneLabelTemplate && (this.widgetAnyoneLabelTemplate.is_multi_language = 1);

    this.widgetAnyoneDescTemplate = this.widgetTemplates.find(template =>
      template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_ANYONE_DESC_LABEL
    );
    this.widgetAnyoneDescTemplate && (this.widgetAnyoneDescTemplate.is_multi_language = 1);

    this.widgetResourceHeadingTemplate = this.widgetTemplates.find(template =>
      template.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_RESOURCE_LABEL
    );
    this.widgetResourceHeadingTemplate && (this.widgetResourceHeadingTemplate.is_multi_language = 1);

    this.stepErrorTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_STEP_ERROR_WORKER
    );
  }
}
